import React, { useEffect, useState } from "react";
import zoomIn from '../assets/zoom.png';
import zoomOut from '../assets/dezoom.png';
import { ZoomIn, ZoomOut, RefreshCircle, Folder } from 'iconoir-react';

import loadingGif from '../assets/loading.gif';
import '../css/ShareFolder.css';

/**
 * Hook personnalisé pour détecter si la largeur de la fenêtre correspond à un mobile.
 *
 * @param {number} breakpoint - largeur max (en px) considérée comme mobile (default: 768)
 * @returns {boolean} true si largeur fenêtre <= breakpoint
 */
function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, [breakpoint]);

    return isMobile;
}

/**
 * Composant affichant l'arborescence d'un dossier partagé,
 * avec gestion du zoom (desktop uniquement), navigation et chargement des dossiers/fichiers.
 *
 * @param {Object} props
 * @param {number|string} props.dossier_id - ID du dossier racine à afficher
 * @param {string[]} props.currentPath - Chemin actuel sous forme de tableau (ex: ["root", "subfolder"])
 * @param {function} props.navigateToPath - Fonction callback appelée lors d'un clic sur un dossier, reçoit un tableau chemin
 */
const ShareFolder = ({ dossier_id, currentPath, baseFolder, navigateToPath }) => {
    const [treeData, setTreeData] = useState(null); // Arborescence chargée adaptée
    const [zoom, setZoom] = useState(1); // Échelle de zoom
    const [openFolders, setOpenFolders] = useState({}); // Dossiers ouverts par leur chemin "path"
    const [selectedPath, setSelectedPath] = useState(''); // Chemin du dossier sélectionné
    const API_URL = process.env.REACT_APP_API_URL;

    const isMobile = useIsMobile();

    /**
     * Adapte un noeud reçu depuis le backend à la structure attendue par le composant
     * @param {Object} node - Noeud brut du backend
     * @returns {Object} noeud adapté { name, type, children }
     */
    const adaptNode = (node) => ({
        name: node.nom,
        type: node.type,
        children: node.children ? node.children.map(adaptNode) : []
    });

    /**
     * Récupère l'arborescence du dossier via l'API,
     * convertit les données reçues en structure adaptée et met à jour treeData.
     */
    const fetchAndBuildTree = async () => {
        try {
            const repertoireString = currentPath[0];
            const res = await fetch(`${API_URL}/api/getArborescence.php`, {
                method: "POST",
                credentials: 'include',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ dossier_id, repertoire: repertoireString }),
            });
            const data = await res.json();
            const tree = data.tree || data;

            if (tree && Array.isArray(tree)) {
                const adaptedTree = tree.map(adaptNode);
                setTreeData(adaptedTree);
            } else {
                setTreeData(null);
            }
        } catch (err) {
            console.error("Erreur lors du chargement :", err);
            setTreeData(null);
        }
    };

    // Fetch à chaque changement de dossier ou chemin
    useEffect(() => {

      
        if (dossier_id !== undefined && currentPath[0] !== undefined) {
            fetchAndBuildTree();
        }
    }, [dossier_id, currentPath]);

    // Ouvre automatiquement les dossiers du premier niveau à la première récupération des données
    useEffect(() => {
        if (treeData && Object.keys(openFolders).length === 0) {
            const initialOpenFolders = {};
            treeData.forEach(node => {
                const safeName = node.name.replace(/\\/g, "/");
                if (node.type === "D") {
                    initialOpenFolders[safeName] = true;
                }
            });
            setOpenFolders(initialOpenFolders);
        }
    }, [treeData]);

    // Met à jour le chemin sélectionné lorsque le chemin courant change (navigation externe)
    useEffect(() => {
        const pathString = currentPath.join('/');
        setSelectedPath(pathString);
        // console.log(currentPath[0]);
        
    }, [currentPath]);

    /**
     * Ouvre ou ferme un dossier donné par son chemin
     * @param {string} path - Chemin complet du dossier
     */
    const toggleFolder = (path) => {
        const newOpenFolders = {};
        const pathParts = path.split('/');
        let currentCumulativePath = '';
        for (const part of pathParts) {
            currentCumulativePath = currentCumulativePath ? `${currentCumulativePath}/${part}` : part;
            newOpenFolders[currentCumulativePath] = true;
        }
        setOpenFolders(newOpenFolders);
    };

    /**
     * Rend récursivement un noeud (dossier ou fichier)
     * @param {Object} node - Noeud à afficher
     * @param {string} path - Chemin accumulé jusqu'au noeud (default "")
     * @returns JSX.Element
     */
    const renderNode = (node, path = "") => {
        const safeName = node.name.replace(/\\/g, "/");
        const currentNodePath = path ? `${path}/${safeName}` : safeName;
        const isOpen = openFolders[currentNodePath];

        return (
            <div key={currentNodePath} className={`folder-block${isOpen ? " open" : " "}`}>
                <div
                    className={`fi-size-xs folder-name${node.type !== "D" ? " file" : ""} ${selectedPath === currentNodePath ? "selected" : ""}`}
                    onClick={() => {
                        if (node.type === "D") {
                            toggleFolder(currentNodePath);
                        }
                        const pathArray = currentNodePath.split('/');
                        navigateToPath(pathArray);
                    }}
                >
                    <Folder width="18" height="18" />
                    <span>{node.name}</span>
                </div>
                {isOpen && node.children?.length > 0 && (
                    <div className="folder-content">
                        {node.children.map(child => renderNode(child, currentNodePath))}
                    </div>
                )}
            </div>
        );
    };
  //🔄 Reset

    return (
        <div>
            {!isMobile && (
              <></>
                // <div className="zoom-controls">
                //     <button onClick={() => setZoom(z => Math.min(z + 0.1, 2))}>
                //         {/* <img src={zoomIn} alt="Zoom In" /> */}
                //         <ZoomIn color="red-" height={24} width={24} />
                //     </button>
                //     <button onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))}>
                //         {/* <img src={zoomOut} alt="Zoom Out" /> */}
                //         <ZoomOut color="red-" height={24} width={24} />
                //     </button>
                    
                //     <button disabled={zoom === 1} onClick={() => setZoom(1)}> <RefreshCircle/></button>
                // </div>
            )}
          {/* <p className="font-bold text-center text-sm">{baseFolder}</p> */}
            <div className="folder-structure-wrapper hidden sm:block ">
                <div className="folder-structure">
                    <div
                        className="folder-content-inner"
                        style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                    >
                        {treeData ? (
                            treeData.map(node => renderNode(node))
                        ) : (
                            <div className="loading-container">
                                <img src={loadingGif} alt="Chargement..." className="loading-gif" />
                                <p>Chargement...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareFolder;
